import {
	type Asset,
	type AssetType,
	fallbackToEmptyObject,
	type Procedure,
	type ProcedureFieldValues,
	type ProcedureFieldValuesAttachment,
	type ProcedureInitiative,
	type ProcedureStep,
	type ProcedureStepAssignee,
	type ProcedureStepFieldValues,
	type ProcedureStepFieldValuesAttachment,
	type ProcedureStepFieldValuesReview,
	type ProcedureStepReviewer,
	type ProcedureType,
	type Project,
	type Stored,
} from "@overmap-ai/common"
import {
	restructureSelectorWithArgs,
	selectProcedureFieldValues,
	selectProcedureFieldValuesAttachments,
	selectProcedureInitiatives,
	selectProcedures,
	selectProcedureStepAssignees,
	selectProcedureStepFieldValues,
	selectProcedureStepFieldValuesAttachments,
	selectProcedureStepFieldValuesReviews,
	selectProcedureStepReviewers,
} from "@overmap-ai/core"
import { createSelector } from "@reduxjs/toolkit"

export const selectProcedureInitiativesOfProjectAndAssetType = restructureSelectorWithArgs(
	createSelector(
		[
			selectProcedureInitiatives,
			(_, projectId: Project["uuid"]) => projectId,
			(_, __, assetTypeId: AssetType["uuid"]) => assetTypeId,
		],
		(assets, projectId, assetTypeId) => {
			return assets.filter(({ project, asset_type }) => project === projectId && asset_type === assetTypeId)
		},
	),
)

export const selectProceduresOfProjectAndProcedureType = restructureSelectorWithArgs(
	createSelector(
		[
			selectProcedures,
			(_, projectId: Project["uuid"]) => projectId,
			(_, __: Project["uuid"], procedureTypeId: ProcedureType["uuid"]) => procedureTypeId,
		],
		(procedures, projectId, procedureTypeId) => {
			return procedures.filter(
				({ project, procedure_type }) => project === projectId && procedure_type === procedureTypeId,
			)
		},
	),
)

export const selectProceduresByAssetOfProcedureInitiative = restructureSelectorWithArgs(
	createSelector(
		[selectProcedures, (_, procedureInitiativeId: ProcedureInitiative["uuid"]) => procedureInitiativeId],
		(procedures, procedureInitiativeId) => {
			const ret: Record<Asset["uuid"], Stored<Procedure> | undefined> = {}

			for (const procedure of procedures) {
				const { asset, procedure_initiative } = procedure
				if (procedure_initiative !== procedureInitiativeId) continue
				ret[asset] = procedure
			}

			return fallbackToEmptyObject(ret)
		},
	),
)

export const selectLatestProcedureFieldValuesByProcedure = createSelector(
	[selectProcedureFieldValues],
	(procedureFieldValues) => {
		const ret: Record<Procedure["uuid"], Stored<ProcedureFieldValues> | undefined> = {}

		for (const fieldValues of procedureFieldValues) {
			const current = ret[fieldValues.procedure]

			if (!current || current.submitted_at < fieldValues.submitted_at) {
				ret[fieldValues.procedure] = fieldValues
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestFieldValuesByProcedureOfProcedureInitiative = restructureSelectorWithArgs(
	createSelector(
		[
			selectProcedures,
			selectProcedureFieldValues,
			(_, procedureInitiativeId: ProcedureInitiative["uuid"]) => procedureInitiativeId,
		],
		(procedures, allFieldValues, procedureInitiativeId) => {
			const ret: Record<Procedure["uuid"], Stored<ProcedureFieldValues> | undefined> = {}

			const procedureIds = new Set(
				procedures
					.filter(({ procedure_initiative }) => procedure_initiative === procedureInitiativeId)
					.map(({ uuid }) => uuid),
			)

			for (const fieldValues of allFieldValues) {
				const { procedure } = fieldValues

				if (!procedureIds.has(procedure)) continue

				const currentFieldValues = ret[procedure]

				if (!currentFieldValues || fieldValues.submitted_at > currentFieldValues.submitted_at) {
					ret[procedure] = fieldValues
				}
			}

			return fallbackToEmptyObject(ret)
		},
	),
)

export const selectAttachmentsByProcedureFieldValues = createSelector(
	[selectProcedureFieldValuesAttachments],
	(procedureFieldValuesAttachments) => {
		const ret: Record<ProcedureFieldValues["uuid"], Stored<ProcedureFieldValuesAttachment>[] | undefined> = {}

		for (const procedureFieldValuesAttachment of procedureFieldValuesAttachments) {
			const { field_values } = procedureFieldValuesAttachment
			if (!(field_values in ret)) ret[field_values] = []
			ret[field_values]!.push(procedureFieldValuesAttachment)
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestFieldValuesByProcedureOfProcedureInitiativeAndProcedureStep = restructureSelectorWithArgs(
	createSelector(
		[
			selectProcedures,
			selectProcedureStepFieldValues,
			(_, procedureInitiativeId: ProcedureInitiative["uuid"]) => procedureInitiativeId,
			(_, __: ProcedureInitiative["uuid"], procedureStepId: ProcedureStep["uuid"]) => procedureStepId,
		],
		(procedures, allFieldValues, procedureInitiativeId, procedureStepId) => {
			const ret: Record<Procedure["uuid"], Stored<ProcedureStepFieldValues> | undefined> = {}

			const procedureIds = new Set(
				procedures
					.filter(({ procedure_initiative }) => procedure_initiative === procedureInitiativeId)
					.map(({ uuid }) => uuid),
			)

			for (const fieldValues of allFieldValues) {
				const { procedure, procedure_step } = fieldValues

				if (procedure_step !== procedureStepId || !procedureIds.has(procedure)) continue

				const current = ret[procedure]

				if (!current || current.submitted_at <= fieldValues.submitted_at) {
					ret[procedure] = fieldValues
				}
			}

			return fallbackToEmptyObject(ret)
		},
	),
)

export const selectAttachmentsByProcedureStepFieldValues = createSelector(
	[selectProcedureStepFieldValuesAttachments],
	(procedureStepFieldValuesAttachments) => {
		const ret: Record<ProcedureStepFieldValues["uuid"], Stored<ProcedureStepFieldValuesAttachment>[]> = {}

		for (const procedureStepFieldValuesAttachment of procedureStepFieldValuesAttachments) {
			const { field_values } = procedureStepFieldValuesAttachment
			if (!(field_values in ret)) ret[field_values] = []
			ret[field_values]!.push(procedureStepFieldValuesAttachment)
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestProcedureStepFieldValuesByProcedure = createSelector(
	[selectProcedureStepFieldValues],
	(procedureStepFieldValues) => {
		const ret: Record<
			Procedure["uuid"],
			Record<ProcedureStep["uuid"], Stored<ProcedureStepFieldValues> | undefined> | undefined
		> = {}

		for (const fieldValues of procedureStepFieldValues) {
			const { procedure, procedure_step } = fieldValues

			if (!(procedure in ret)) ret[procedure] = {}

			const current = ret[procedure]![procedure_step]

			if (!current || current.submitted_at < fieldValues.submitted_at) {
				ret[procedure]![procedure_step] = fieldValues
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectLatestProcedureStepFieldValuesReviewByFieldValues = createSelector(
	[selectProcedureStepFieldValuesReviews],
	(procedureStepFieldValuesReviews) => {
		const ret: Record<ProcedureStepFieldValues["uuid"], Stored<ProcedureStepFieldValuesReview> | undefined> = {}

		for (const review of procedureStepFieldValuesReviews) {
			const current = ret[review.field_values]

			if (!current || current.submitted_at < review.submitted_at) {
				ret[review.field_values] = review
			}
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectProcedureStepAssigneesByProcedure = createSelector(
	[selectProcedureStepAssignees],
	(procedureStepAssignees) => {
		const ret: Record<
			Procedure["uuid"],
			Record<ProcedureStep["uuid"], Stored<ProcedureStepAssignee>[] | undefined> | undefined
		> = {}

		for (const procedureStepAssignee of procedureStepAssignees) {
			const { procedure, procedure_step } = procedureStepAssignee

			if (!(procedure in ret)) ret[procedure] = {}
			if (!(procedure_step in ret[procedure]!)) ret[procedure]![procedure_step] = []
			ret[procedure]![procedure_step]!.push(procedureStepAssignee)
		}

		return fallbackToEmptyObject(ret)
	},
)

export const selectProcedureStepReviewersByProcedure = createSelector(
	[selectProcedureStepReviewers],
	(procedureStepReviewers) => {
		const ret: Record<
			Procedure["uuid"],
			Record<ProcedureStep["uuid"], Stored<ProcedureStepReviewer>[] | undefined> | undefined
		> = {}

		for (const procedureStepReviewer of procedureStepReviewers) {
			const { procedure, procedure_step } = procedureStepReviewer

			if (!(procedure in ret)) ret[procedure] = {}
			if (!(procedure_step in ret[procedure]!)) ret[procedure]![procedure_step] = []
			ret[procedure]![procedure_step]!.push(procedureStepReviewer)
		}

		return fallbackToEmptyObject(ret)
	},
)
